import React, { useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import Loading from '../Loading';
import ConfirmationModal from '../modals/ConfirmationModal';
import CreateEditPrivilegedAccessModal from './CreateEditPrivilegedAccessModal';
import { getPrivilegedAccessEntries, deletePrivilegedAccessEntry, updatePrivilegedAccessEntry } from '../../server/server';
import { notify } from '../../utils/util';
import { ReactComponent as DotsIcon } from '../../assets/svg/DotsIcon.svg';
import { FaEdit, FaTrash, FaToggleOn, FaToggleOff } from 'react-icons/fa';

const limit = 20;

const filters = [
	{ label: 'All', value: 'all' },
	{ label: 'Active', value: 'true' },
	{ label: 'Inactive', value: 'false' },
];

function PrivilegedAccessHome() {
	const [entries, setEntries] = useState<any[]>([]);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [resultsNum, setResultsNum] = useState(0);
	const [activeFilter, setActiveFilter] = useState('all');
	const [selectedEntry, setSelectedEntry] = useState<any | null>(null);
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const [createEditModalOpen, setCreateEditModalOpen] = useState(false);
	const totalEntries = useRef(0);

	useEffect(() => {
		loadEntries();
	}, [resultsNum]);

	const loadEntries = async (filter: string = activeFilter) => {
		const isInitialLoad = resultsNum === 0;

		try {
			isInitialLoad ? setLoading(true) : setLoadingMore(true);

			const resp = await getPrivilegedAccessEntries(limit, resultsNum, filter);

			if (resp.status !== 200) return;

			const data = Array.isArray(resp.data) ? resp.data : [];
			totalEntries.current = isInitialLoad ? data.length : entries.length + data.length;

			setHasMore(data.length >= limit);
			setEntries((prev) => (isInitialLoad ? data : [...prev, ...data]));
		} catch (error) {
			console.error('Failed to load entries:', error);
		} finally {
			setLoading(false);
			setLoadingMore(false);
		}
	};

	const reloadList = () => {
		if (resultsNum === 0) loadEntries();
		else setResultsNum(0);
	};

	const handleFilterChange = (value: string) => {
		setActiveFilter(value);
		setEntries([]);
		setResultsNum(0);
		// loadEntries will be triggered by the resultsNum useEffect
		// But if resultsNum is already 0, we need to call manually
		setTimeout(() => {
			loadEntries(value);
		}, 0);
	};

	const openCreateModal = () => {
		setSelectedEntry(null);
		setCreateEditModalOpen(true);
	};

	const openEditModal = (entry: any) => {
		setSelectedEntry(entry);
		setCreateEditModalOpen(true);
	};

	const openDeleteModal = (entry: any) => {
		setSelectedEntry(entry);
		setDeleteModalOpen(true);
	};

	const closeDeleteModal = () => {
		setDeleteModalOpen(false);
	};

	const closeCreateEditModal = () => {
		setCreateEditModalOpen(false);
	};

	const removeEntry = async () => {
		if (selectedEntry) {
			const resp = await deletePrivilegedAccessEntry(selectedEntry.id);
			if (resp.status === 200) {
				setEntries((prev) => prev.filter((e) => e.id !== selectedEntry.id));
				totalEntries.current -= 1;
				notify('Entry deleted successfully');
			} else {
				notify('Failed to delete entry');
			}
		}
	};

	const toggleActive = async (entry: any) => {
		const newActive = !entry.active;
		const resp = await updatePrivilegedAccessEntry(entry.id, { active: newActive });
		if (resp.status === 200) {
			setEntries((prev) => prev.map((e) => (e.id === entry.id ? { ...e, active: newActive } : e)));
			notify(newActive ? 'Entry activated' : 'Entry deactivated');
		} else {
			notify('Failed to update entry');
		}
	};

	return (
		<div id='scroll-inf-task' className='flex h-screen flex-col items-center overflow-y-auto bg-[#ECEBF8] py-[40px]'>
			<div className='w-full max-w-[1200px] px-[10px]'>
				<div className='flex min-h-[calc(100vh-80px)] w-full flex-col rounded-[10px] bg-white px-[40px] py-[30px]'>
					{/* Header */}
					<div className='mb-[30px]'>
						<p className='text-[40px] font-bold leading-[50px] text-darkViolet'>Privileged Access</p>
						<p className='mt-[8px] text-[14px] font-medium leading-5 text-mudPurple'>Manage email addresses and domains that grant premium subscriptions at sign-up</p>
					</div>

					{/* Controls */}
					<div className='mb-[30px] flex flex-row items-center gap-4'>
						<div className='flex flex-1 flex-row items-center gap-2'>
							<p className='text-[20px] font-semibold leading-6 text-primaryViolet'>Total:</p>
							<p className='text-[20px] font-semibold leading-6 text-mudPurple'>{totalEntries.current}</p>
						</div>

						{/* Filter Pills */}
						<div className='flex flex-row rounded-full border border-deselectPurple p-1 gap-1'>
							{filters.map((f) => (
								<div key={f.value} className={`px-5 py-2 rounded-full cursor-pointer text-[14px] font-semibold transition ${activeFilter === f.value ? 'bg-primaryViolet text-brandYellow' : 'text-mudPurple hover:bg-[#F8F8FB]'}`} onClick={() => handleFilterChange(f.value)}>
									{f.label}
								</div>
							))}
						</div>

						{/* Add New Button */}
						<div className='flex h-[50px] min-w-[180px] cursor-pointer items-center justify-center rounded-full border border-primaryViolet bg-white px-3 text-[18px] font-semibold leading-6 text-primaryViolet transition hover:bg-primaryViolet hover:text-brandYellow' onClick={openCreateModal}>
							Add new
						</div>
					</div>

					{/* Empty State */}
					{entries.length === 0 && !loading && (
						<div className='flex w-full flex-grow items-center justify-center'>
							<p className='text-center text-[14px] font-medium leading-5 text-mudPurple'>
								This list is empty right now.
								<br /> Consider adding new privileged access entries...
							</p>
						</div>
					)}

					{/* Table */}
					{!loading && entries.length !== 0 && (
						<InfiniteScroll
							dataLength={entries.length}
							next={() => {
								setResultsNum(entries.length);
							}}
							hasMore={hasMore}
							scrollableTarget='scroll-inf-task'
							className='flex-1 flex flex-col'
							inverse={false}
							loader={undefined}
							style={{ display: 'flex', flexDirection: 'column', flex: 1 }}
						>
							<div className='w-full rounded-[10px] border border-[#EAECF0]'>
								{/* Table Header */}
								<div className='grid w-full grid-cols-[8%_22%_22%_16%_12%_12%_8%] bg-[#F9FAFB] px-6 py-3'>
									<div className='text-[14px] font-semibold leading-5 text-[#475467]'>Status</div>
									<div className='text-[14px] font-semibold leading-5 text-[#475467]'>Emails</div>
									<div className='text-[14px] font-semibold leading-5 text-[#475467]'>Domains</div>
									<div className='text-[14px] font-semibold leading-5 text-[#475467]'>Subscription</div>
									<div className='text-[14px] font-semibold leading-5 text-[#475467]'>Period</div>
									<div className='text-[14px] font-semibold leading-5 text-[#475467]'>Created</div>
									<div className='text-center text-[14px] font-semibold leading-5 text-[#475467]'>Actions</div>
								</div>

								{/* Table Rows */}
								{entries.map((entry: any, index: number) => (
									<PrivilegedAccessRow key={entry.id} entry={entry} isLast={index === entries.length - 1} onEdit={openEditModal} onDelete={openDeleteModal} onToggleActive={toggleActive} />
								))}
							</div>

							{/* Info Note */}
							<p className='mt-3 text-[12px] text-mudPurple mb-14'>Note: Deleting an entry does not revoke subscriptions already granted to users.</p>
						</InfiniteScroll>
					)}

					{/* Loading State */}
					{loading && (
						<div className='flex w-full flex-grow items-center justify-center'>
							<Loading size={70} primaryColor='#5E639B' stroke={3} />
						</div>
					)}
				</div>
			</div>

			{/* Modals */}
			<ConfirmationModal modalIsOpen={deleteModalOpen} closeModal={closeDeleteModal} confirmation={removeEntry} message={'Do you want to delete this privileged access entry?'} subMessage='The entry will be permanently deleted. Already granted subscriptions will not be revoked.' />
			<CreateEditPrivilegedAccessModal modalIsOpen={createEditModalOpen} closeModal={closeCreateEditModal} entry={selectedEntry} onSuccess={reloadList} />
		</div>
	);
}

const PrivilegedAccessRow = ({ entry, isLast, onEdit, onDelete, onToggleActive }: { entry: any; isLast: boolean; onEdit: (entry: any) => void; onDelete: (entry: any) => void; onToggleActive: (entry: any) => void }) => {
	const emailsDisplay = (entry.emails || []).join(', ');
	const domainsDisplay = (entry.domains || []).join(', ');
	const period = entry.periodDuration && entry.periodFrame ? `${entry.periodDuration} ${entry.periodFrame}` : '-';
	const created = entry.createdAt
		? new Date(entry.createdAt).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
			})
		: '-';

	const hasNoTargets = (!entry.emails || entry.emails.length === 0) && (!entry.domains || entry.domains.length === 0);

	return (
		<div className={`grid w-full grid-cols-[8%_22%_22%_16%_12%_12%_8%] px-6 py-4 transition-colors hover:bg-[#F9FAFB] border-[#EAECF0] ${!isLast ? 'border-b' : ''}`}>
			{/* Status */}
			<div className='flex items-center'>
				<span className={`rounded-full px-3 py-1 text-[12px] font-semibold ${entry.active ? 'bg-[#ECFDF3] text-[#027A48]' : 'bg-[#F2F4F7] text-[#344054]'}`}>{entry.active ? 'Active' : 'Inactive'}</span>
			</div>

			{/* Emails */}
			<div className='flex items-center pr-2'>
				<p className='text-[14px] font-normal leading-5 text-[#475467] truncate' title={emailsDisplay}>
					{emailsDisplay || <span className='text-mudPurple italic'>None</span>}
				</p>
			</div>

			{/* Domains */}
			<div className='flex items-center pr-2'>
				<p className='text-[14px] font-normal leading-5 text-[#475467] truncate' title={domainsDisplay}>
					{domainsDisplay || <span className='text-mudPurple italic'>None</span>}
				</p>
			</div>

			{/* Subscription */}
			<div className='flex items-center'>
				<p className='text-[14px] font-normal leading-5 text-[#475467]'>{entry.subscriptionName || '-'}</p>
			</div>

			{/* Period */}
			<div className='flex items-center'>
				<p className='text-[14px] font-normal leading-5 text-[#475467]'>{period}</p>
			</div>

			{/* Created */}
			<div className='flex items-center'>
				<p className='text-[14px] font-normal leading-5 text-[#475467]'>{created}</p>
			</div>

			{/* Actions */}
			<div className='flex items-center justify-center' onClick={(e) => e.stopPropagation()}>
				<PrivilegedAccessActions entry={entry} onEdit={onEdit} onDelete={onDelete} onToggleActive={onToggleActive} />
			</div>

			{/* Warning for entries with no emails and no domains */}
			{hasNoTargets && (
				<div className='col-span-7 mt-1'>
					<p className='text-[11px] text-redError italic'>This entry has no emails or domains and won't match any users.</p>
				</div>
			)}
		</div>
	);
};

const PrivilegedAccessActions = ({ entry, onEdit, onDelete, onToggleActive }: { entry: any; onEdit: (entry: any) => void; onDelete: (entry: any) => void; onToggleActive: (entry: any) => void }) => {
	const [optionsVisible, setOptionsVisible] = useState(false);

	return (
		<div className='flex justify-center items-center cursor-pointer relative' onMouseEnter={() => setOptionsVisible(true)} onMouseLeave={() => setOptionsVisible(false)}>
			<div className='hover:bg-gray300 p-1 rounded-[4px]'>
				<DotsIcon />
			</div>
			{optionsVisible && (
				<div className='absolute w-[200px] p-[4px] rounded-[8px] top-0 right-[30px] bg-white border border-gray300 flex flex-col cursor-default z-[10] shadow-lg'>
					<div
						className='text-[14px] leading-5 font-medium text-gray600 cursor-pointer flex flex-row items-center gap-2 px-2 py-2 hover:text-gray900 hover:bg-gray50 rounded-[6px] transition'
						onClick={() => {
							setOptionsVisible(false);
							onEdit(entry);
						}}
					>
						<FaEdit />
						Edit
					</div>
					<div
						className='text-[14px] leading-5 font-medium text-gray600 cursor-pointer flex flex-row items-center gap-2 px-2 py-2 hover:text-gray900 hover:bg-gray50 rounded-[6px] transition'
						onClick={() => {
							setOptionsVisible(false);
							onToggleActive(entry);
						}}
					>
						{entry.active ? <FaToggleOff /> : <FaToggleOn />}
						{entry.active ? 'Deactivate' : 'Activate'}
					</div>
					<div
						className='text-[14px] leading-5 font-medium text-gray600 cursor-pointer flex flex-row items-center gap-2 px-2 py-2 hover:text-gray900 hover:bg-gray50 rounded-[6px] transition'
						onClick={() => {
							setOptionsVisible(false);
							onDelete(entry);
						}}
					>
						<FaTrash />
						Delete
					</div>
				</div>
			)}
		</div>
	);
};

export default PrivilegedAccessHome;
